import { useEffect, FC, useMemo, useState, useCallback, useLayoutEffect } from 'react';
import { useIntl, defineMessages } from 'react-intl';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import Loader from '../../components/Loader/Loader';
import Content from '../../components/Content/Content';
import FormError from '../../components/FormError/FormError';
import { useAppDispatch } from '../../redux/Store';
import { RemoteData, RemoteStatus } from '../../redux/SharedTypes';
import { fetchCurrencies, fetchProject } from '../../redux/PublicFormSlice/PublicFormThunks';
import { getCurrency, getProject } from '../../redux/PublicFormSlice/PublicFormSelectors';
import PublicFormView from './PublicFormView/PublicFormView';
import { getErrorMessageOrDefault } from '../../helpers/errorMessageHelper/errorMessageHelper';
import PublicFormContext from './Context/PublicFormContext';
import { PublicForm as PublicFormData } from '../../redux/PublicFormSlice/PublicFormSlice';
import MerchantOrder from '../../interfaces/MerchantOrder';
import MerchantPreOrder from '../../interfaces/MerchantPreOrder';

const messages = defineMessages({
	defaultErrorMessage: {
		id: 'payments.some_errors_occured',
		defaultMessage: 'Some errors occurred',
	},
});

const PublicForm: FC = () => {
	const { formatMessage } = useIntl();
	const dispatch = useAppDispatch();
	const { id = '' } = useParams<{ id?: string }>();
	const {
		status: projectStatus,
		error: projectError,
		data: projectData,
	} = useSelector(getProject);
	const { status: currencyStatus, error: currencyError } = useSelector(getCurrency);
	const [form, setForm] = useState<PublicFormData>({
		description: null,
		orderId: null,
		receiveAmount: '',
		receiveCurrencyCode: null,
	});
	const [action, setAction] = useState<RemoteData<MerchantOrder | MerchantPreOrder>>({
		status: RemoteStatus.None,
	});
	const updateForm = useCallback((value: Partial<PublicFormData>) => {
		setAction({ status: RemoteStatus.None });
		setForm((x) => ({ ...x, ...value }));
	}, []);

	const content = useMemo(() => {
		if ([projectStatus, currencyStatus].some((x) => x === RemoteStatus.InProgress))
			return <Loader />;

		if ([projectStatus, currencyStatus].some((x) => x === RemoteStatus.Error))
			return (
				<Content name={formatMessage(messages.defaultErrorMessage)} showBadge={false}>
					<FormError
						message={formatMessage(
							getErrorMessageOrDefault(projectError || currencyError)
						)}
					/>
				</Content>
			);

		if ([projectStatus, currencyStatus].every((x) => x === RemoteStatus.Done))
			return <PublicFormView />;

		return null;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [projectStatus, currencyStatus]);

	useEffect(() => {
		if (currencyStatus === RemoteStatus.None) dispatch(fetchCurrencies());
	}, [dispatch, id, currencyStatus]);

	useEffect(() => {
		if (projectStatus === RemoteStatus.None) dispatch(fetchProject(id));
	}, [dispatch, id, projectStatus]);

	useLayoutEffect(() => {
		if (projectStatus === RemoteStatus.Done && projectData!.currencyCode)
			updateForm({ receiveCurrencyCode: projectData!.currencyCode });
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [projectStatus]);

	// eslint-disable-next-line react/jsx-no-constructed-context-values
	const state = { form, updateForm, action, setAction };

	return <PublicFormContext.Provider value={state}>{content}</PublicFormContext.Provider>;
};

export default PublicForm;
